import { useQuery, type QueryKey } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { useDelayedFlag, useDocumentVisibility } from '@/shared/hooks';
import { defaultStaleTimeMs } from './gridDefaults';
import type { GridQueryInput, GridQueryResult } from './gridUtils';
import type { ApiError } from '@/shared/data/client';
import type { GridValidRowModel } from '@mui/x-data-grid';

type UseGridQueryLifecycleParams<T extends GridValidRowModel> = {
  finalQueryKey: QueryKey;
  isAuthenticated: boolean;
  queryFn: (input: GridQueryInput) => Promise<GridQueryResult<T>>;
  initialData?: GridQueryResult<T>;
  queryInput: GridQueryInput;
  preservePreviousData?: boolean;
  staleTimeMs?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean | 'always';
  refetchIntervalMs?: number | false;
};

type UseGridQueryLifecycleResult<T extends GridValidRowModel> = {
  query: ReturnType<typeof useQuery<GridQueryResult<T>, ApiError>>;
  rowsData: T[];
  rowCount: number;
  showInitialSkeleton: boolean;
  showFetchSkeleton: boolean;
  lastUpdatedAt: number | null;
  queryIsFetching: boolean;
  queryIsFetched: boolean;
};

export const shouldShowInitialGridSkeleton = (input: {
  hasFetchedOnce: boolean;
  isFetching: boolean;
  lastSettledQueryKey: string | null;
  rowsLength: number;
  currentQueryKey: string;
  delayedInitialSkeleton: boolean;
}): boolean => {
  if (input.delayedInitialSkeleton) {
    return true;
  }

  if (!input.hasFetchedOnce || !input.isFetching || input.rowsLength > 0) {
    return false;
  }

  return input.lastSettledQueryKey !== input.currentQueryKey;
};

export const getGridPlaceholderData = <T>(
  preservePreviousData?: boolean,
): ((previousData: T | undefined) => T | undefined) | undefined =>
  preservePreviousData ? (previousData) => previousData : undefined;

export const useGridQueryLifecycle = <T extends GridValidRowModel>({
  finalQueryKey,
  isAuthenticated,
  queryFn,
  initialData,
  queryInput,
  preservePreviousData,
  staleTimeMs,
  refetchOnWindowFocus,
  refetchOnMount,
  refetchIntervalMs,
}: UseGridQueryLifecycleParams<T>): UseGridQueryLifecycleResult<T> => {
  const finalQueryKeyString = useMemo(() => JSON.stringify(finalQueryKey), [finalQueryKey]);
  const isDocumentVisible = useDocumentVisibility();
  const query = useQuery<GridQueryResult<T>, ApiError>({
    queryKey: finalQueryKey,
    enabled: isAuthenticated,
    queryFn: async (): Promise<GridQueryResult<T>> => {
      return await queryFn(queryInput);
    },
    placeholderData: getGridPlaceholderData<GridQueryResult<T>>(preservePreviousData),
    initialData,
    staleTime: staleTimeMs ?? defaultStaleTimeMs,
    refetchOnWindowFocus: refetchOnWindowFocus ?? false,
    refetchOnReconnect: true,
    refetchOnMount: refetchOnMount ?? false,
    refetchInterval: isDocumentVisible ? refetchIntervalMs : false,
  });

  const emptyRows = useMemo<T[]>(() => [], []);
  const rowsData: T[] = query.data?.items ?? emptyRows;
  const hasFetchedOnceRef = useRef(Boolean(initialData));
  useEffect(() => {
    if (!query.isFetched) return;
    hasFetchedOnceRef.current = true;
  }, [query.isFetched]);
  useEffect(() => {
    if (isAuthenticated) return;
    hasFetchedOnceRef.current = Boolean(initialData);
  }, [initialData, isAuthenticated]);
  const lastSettledQueryKeyRef = useRef<string | null>(initialData ? finalQueryKeyString : null);
  useEffect(() => {
    if (query.status !== 'success') return;
    lastSettledQueryKeyRef.current = finalQueryKeyString;
  }, [finalQueryKeyString, query.status]);
  const lastRowCountRef = useRef<number>(0);
  useEffect(() => {
    if (!query.data) return;
    lastRowCountRef.current = query.data.total;
  }, [query.data]);
  const rowCount = query.data?.total ?? (query.isFetching ? lastRowCountRef.current : 0);
  const delayedInitialSkeleton = useDelayedFlag(
    query.isFetching && !hasFetchedOnceRef.current,
    800,
  );
  const showInitialSkeleton = shouldShowInitialGridSkeleton({
    hasFetchedOnce: hasFetchedOnceRef.current,
    isFetching: query.isFetching,
    lastSettledQueryKey: lastSettledQueryKeyRef.current,
    rowsLength: rowsData.length,
    currentQueryKey: finalQueryKeyString,
    delayedInitialSkeleton,
  });
  const showFetchSkeleton =
    query.isFetching &&
    hasFetchedOnceRef.current &&
    lastSettledQueryKeyRef.current !== null &&
    lastSettledQueryKeyRef.current !== finalQueryKeyString;
  const lastUpdatedAt = query.dataUpdatedAt > 0 ? query.dataUpdatedAt : null;

  return {
    query,
    rowsData,
    rowCount,
    showInitialSkeleton,
    showFetchSkeleton,
    lastUpdatedAt,
    queryIsFetching: query.isFetching,
    queryIsFetched: query.isFetched,
  };
};
